/* GrowthOS — Add to Campaign modal for selected leads.
   UI ported from the claude.ai/design "growthos" BulkModals.AddCampaign; wired to
   the app's real apiServices.addCampaignFromLead. The design only picks a campaign,
   so the member status defaults to "Planned" (the same default the legacy modal
   auto-selected). Reuses the shared .gos-import-* / .gos-tkt-* / .gos-camp-* shell. */
import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import apiServices from '../../services/service';

const PATHS = {
  campaign: 'M3 11v2a1 1 0 0 0 1 1h2l4 4V6L6 10H4a1 1 0 0 0-1 1ZM15 8a4 4 0 0 1 0 8M18 5a8 8 0 0 1 0 14',
  x: 'M18 6 6 18M6 6l12 12',
  check: 'M20 6 9 17l-5-5',
  search: 'M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16ZM21 21l-4.3-4.3',
  info: 'M12 16v-4M12 8h.01M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z',
};
const Ic = ({ name, size = 16, className, style }) => (
  <svg className={className} style={style} viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d={PATHS[name]} />
  </svg>
);

const IC_COLORS = ['#c96342', '#2e63a6', '#2c7a45', '#9a751c', '#6a4fae', '#b34480'];
const icColor = (name) =>
  IC_COLORS[
    String(name || '')
      .split('')
      .reduce((a, c) => a + c.charCodeAt(0), 0) % IC_COLORS.length
  ];

const LeadsAddCampaignModal = ({
  onClose,
  leadIds = [],
  campaigns = [],
  memberStatuses = [],
  onSearch,
  isLoading = false,
  onAdded,
}) => {
  const [sel, setSel] = useState(null);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);

  const items = useMemo(
    () =>
      (campaigns || []).map((c) => ({
        id: c.id,
        name: c.campaignName || c.name || 'Untitled campaign',
        sub: c.campaignType || c.type || c.status || '',
      })),
    [campaigns]
  );
  const list = q.trim()
    ? items.filter((c) => (c.name + ' ' + c.sub).toLowerCase().includes(q.toLowerCase().trim()))
    : items;
  const selCamp = items.find((c) => c.id === sel);

  const onSearchChange = (val) => {
    setQ(val);
    // let the paginated dropdown hit the server too (so results aren't limited to the loaded page)
    if (onSearch) onSearch(val);
  };

  const submit = () => {
    if (!sel || loading) return;
    setLoading(true);
    const planned =
      (memberStatuses || []).find((m) => String(m.displayName).toLowerCase() === 'planned') ||
      (memberStatuses || [])[0];
    const payload = {
      campaignId: [sel],
      leadId: leadIds,
      memberStatus: planned ? planned.id : undefined,
      memberStatusId: planned ? planned.displayName : undefined,
    };
    apiServices
      .addCampaignFromLead(payload)
      .then((response) => {
        if (response.status === 200) {
          if (response?.data === 'No new records inserted (duplicates exist)') {
            toast.error('No new records inserted (duplicates exist)', { position: 'top-right' });
            setLoading(false);
          } else {
            toast.success('Campaign added successfully!', { position: 'top-right' });
            if (onAdded) onAdded();
            onClose();
          }
        } else {
          setLoading(false);
          const msgs = [];
          for (const key in response.errors) {
            if (response.errors.hasOwnProperty(key)) msgs.push(...response.errors[key]);
          }
          msgs.forEach((m) => toast.error(m, { position: 'top-right' }));
        }
      })
      .catch(() => {
        toast.error('An unexpected error occurred.', { position: 'top-right' });
        setLoading(false);
      });
  };

  return (
    <div className="gos-import-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget && !loading) onClose(); }}>
      <div className="gos-tkt-modal gos-bulk-modal" role="dialog" aria-modal="true" aria-label="Add leads to campaign">
        <div className="gos-import-head">
          <div className="gos-import-head-l">
            <span className="gos-import-step-ic"><Ic name="campaign" size={17} /></span>
            <div>
              <div className="gos-import-title">Add to Campaign</div>
              <div className="gos-import-sub">Enroll leads in a sequence</div>
            </div>
          </div>
          <button type="button" className="gos-import-close" onClick={onClose} aria-label="Close" disabled={loading}>
            <Ic name="x" size={18} />
          </button>
        </div>

        <div className="gos-tkt-body">
          {leadIds.length > 0 && (
            <div className="gos-tkt-scope">
              <Ic name="check" size={13} />
              Applies to <strong>{leadIds.length} selected lead{leadIds.length > 1 ? 's' : ''}</strong>
            </div>
          )}

          <div className="gos-camp-search">
            <Ic name="search" size={16} />
            <input
              autoFocus
              placeholder={`Search ${items.length} campaigns…`}
              value={q}
              onChange={(e) => onSearchChange(e.target.value)}
            />
            {q ? (
              <button type="button" className="gos-camp-search-clear" onClick={() => onSearchChange('')} aria-label="Clear">
                <Ic name="x" size={14} />
              </button>
            ) : null}
          </div>

          <div className="gos-camp-meta">
            <span className="gos-bulk-label" style={{ margin: 0 }}>
              {list.length}
              {q ? ` match${list.length !== 1 ? 'es' : ''}` : ' campaigns'}
            </span>
            {selCamp ? (
              <span className="gos-camp-tag">
                <Ic name="check" size={12} />
                {selCamp.name}
              </span>
            ) : null}
          </div>

          <div className="gos-camp-list">
            {isLoading && list.length === 0 ? (
              <div className="gos-camp-empty">
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                <div className="gos-owner-sub" style={{ marginTop: 8 }}>Loading campaigns…</div>
              </div>
            ) : list.length === 0 ? (
              <div className="gos-camp-empty">
                <Ic name="search" size={22} />
                <div style={{ fontWeight: 600, fontSize: 13.5, marginTop: 8 }}>No campaigns found</div>
                <div className="gos-owner-sub" style={{ marginTop: 2 }}>Try a different search term</div>
              </div>
            ) : (
              list.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className={'gos-owner-row' + (sel === c.id ? ' on' : '')}
                  onClick={() => setSel(c.id)}
                >
                  <span className="gos-camp-ic" style={{ background: icColor(c.name) }}>
                    <Ic name="campaign" size={16} />
                  </span>
                  <div className="gos-bulk-grow" style={{ minWidth: 0 }}>
                    <div className="gos-owner-name gos-camp-name">{c.name}</div>
                    {c.sub ? <div className="gos-owner-sub">{c.sub}</div> : null}
                  </div>
                  <span className="gos-opt-radio">{sel === c.id ? <span className="gos-opt-radio-dot" /> : null}</span>
                </button>
              ))
            )}
          </div>

          <div className="gos-bulk-note">
            <Ic name="info" size={14} />
            Leads already in this campaign will be skipped automatically.
          </div>
        </div>

        <div className="gos-import-foot end">
          <div className="gos-foot-actions">
            <button type="button" className="gos-btn gos-btn-ghost" onClick={onClose} disabled={loading}>Cancel</button>
            <button type="button" className="gos-btn gos-btn-primary" onClick={submit} disabled={!sel || loading}>
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Adding…
                </>
              ) : (
                <>
                  <Ic name="check" size={16} /> Add to campaign
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadsAddCampaignModal;
